import { HttpParams } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FileAction, FileColumnKey, FileModel, FolderViewModel, ListResponse, ListSearch, ResultGroup, SortOption } from '@ca/core/models';
import { ExperimentalFeaturesService, ResultsService } from '@ca/core/services';
import { mapAllGroupResultsToFolders } from '@ca/utils';
import { Observable, Subject, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FileColumn } from 'src/app/core/models/file-column.model';
import { OptionItem } from 'src/app/core/models/option-item.model';
import { FILE_EXPLORER_MODE, defaultPageSize } from '../../constants/constants';
import { FileExplorerHistoryService } from '../../services/file-explorer-history.service';
import { TabsetComponent } from '../tabset/tabset.component';

@Component({
    selector: 'ca-file-explorer',
    templateUrl: './file-explorer.component.html',
    styleUrls: ['./file-explorer.component.scss'],
    providers: [FileExplorerHistoryService],
})
export class FileExplorerComponent implements OnInit {
    @Input() startInTab: FileExplorerTabs = FileExplorerTabs.MY_FILES;
    @Input() currentView: 'runner' | 'dataset' = 'runner';
    @Input() tabsToHide: FileExplorerTabs[] = [];
    @Input() foldersToHideIds: (string | null)[];
    @Input() truncateFileNameAtWidthOf?: number;
    @Input() shouldBackButtonBeShown = true;
    @Input() isFilesStatusShown?: boolean;
    @Input() isUploadModal: boolean = false;

    @Output() onFileSelected = new EventEmitter<FileModel>();
    @Output() onMultipleFilesSelected = new EventEmitter<FileModel[]>();
    @Output() onButtonClicked = new EventEmitter<void>();

    @ViewChild('tabset', { read: TabsetComponent }) tabset: TabsetComponent;

    FileExplorerTabs = FileExplorerTabs;
    FILE_EXPLORER_MODE = FILE_EXPLORER_MODE;

    // General setup
    defaultPageSize = defaultPageSize;
    allFolders: FolderViewModel[] = [];
    foldersToShow: FolderViewModel[] = [];
    filesToShow: FileModel[] = [];

    actionsForFilesTable: FileAction[] = ['view'];
    columnsForFilesTable: FileColumn<FileColumnKey>[] = [
        { key: 'name' },
        {
            key: 'sizeInBytes',
            translationKey: 'GENERAL.COLUMN.SIZE',
            defaultValue: '\u2014',
            transform: 'fileSize',
        },
        {
            key: 'type',
            translationKey: 'GENERAL.COLUMN.TYPE',
            defaultValue: '\u2014',
            transform: 'lowercase',
        },
        { key: 'actions' },
    ];

    // Results
    isResultsLoading: boolean = false;
    resultsTotalCount = 0;
    resultSortOptions: OptionItem<SortOption>[] = [
        { label: 'Sort by date desc', value: '-finishedAt' },
        { label: 'Sort by date asc', value: 'finishedAt' },
        { label: 'Sort by A-Z', value: 'title' },
        { label: 'Sort by Z-A', value: '-title' },
        { label: 'Sort by State', value: 'state' },
    ];

    /**
     * Displays error messages.
     */
    errorMessage: string | undefined;
    isMEDProjectEnabled = this.experimentalFeaturesService.isMEDProjectActive;

    private subscriptions: Subscription[] = [];

    private currentResultSearchParams = this.getDefaultResultSearchParams();

    private reloadMachineLearningTab$$ = new Subject<void>();

    get reloadMachineLearningTab$(): Observable<void> {
        return this.reloadMachineLearningTab$$.asObservable();
    }

    /**
     * Shows the number which is relevant for pagination for My Results.
     * When we're in root, we show the total number of all results.
     * In a subfolder, we show the number of the currently shown folders and files, because
     * there everything is on client side
     */
    get numberOfFilesAndFolders(): number {
        let numberToShow = this.filesToShow.length + this.foldersToShow.length;
        if (this.isResultRootFolder) {
            numberToShow = this.resultsTotalCount;
        }

        return numberToShow;
    }

    get activeTab(): string | undefined {
        return this.tabset?.getActiveTabId();
    }

    get isResultRootFolder(): boolean {
        return this.fileExplorerHistoryService.getHistory().length === 0;
    }

    get currentFolder(): FolderViewModel | undefined {
        return this.fileExplorerHistoryService.lastFolderOfHistory();
    }

    constructor(
        private resultsService: ResultsService,
        private fileExplorerHistoryService: FileExplorerHistoryService,
        private experimentalFeaturesService: ExperimentalFeaturesService,
    ) {}

    ngOnInit(): void {
        // Handle initial tab change: Loads data if this is the initially loaded tab
        this.openTab(this.startInTab);
    }

    getNumberOfFilesOfAllSubfolders(folder: FolderViewModel): number {
        let files = 0;
        files += folder.files.length;
        if (folder.subfolders.length > 0) {
            folder.subfolders.forEach((subFol: FolderViewModel) => {
                files += subFol.files.length;
                subFol.subfolders.forEach((f: FolderViewModel) => {
                    files += f.files.length;
                });
            });
        }

        return files;
    }

    onTriggerResultSearch(searchParams: ListSearch): void {
        this.currentResultSearchParams = this.mapSearchParamsToHttpParams(searchParams);
        this.searchResults();
    }

    selectFile(file: FileModel): void {
        this.onFileSelected.emit(file);
    }

    selectMultipleFiles(files: FileModel[]): void {
        this.onMultipleFilesSelected.emit(files);
    }

    /**
     * Opens the tab and prepares the data.
     * Machine Learning tab has its own component, so we don't handle the opening of the tab here, but inside of the component.
     * Here we just trigger an event so that it should reload.
     *
     * @param tabId which tab to open
     */
    openTab(tabId: string): void {
        this.cancelCurrentRequests();
        this.clearFoldersAndFiles();
        this.clearErrorMessage();
        this.fileExplorerHistoryService.clearHistory();

        switch (tabId) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
            case FileExplorerTabs.MY_RESULTS:
                this.searchResults();
                break;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
            case FileExplorerTabs.MY_DATASETS:
                this.reloadMachineLearningTab$$.next();
                break;
        }
    }

    /**
     * Opens a folder. If a folder is provided, opens it. If not, opens an overview over all folders
     */
    openFolder(folder?: FolderViewModel, openFolderFromBreadcrumbClick = false): void {
        if (folder != null) {
            this.foldersToShow = folder.subfolders;
            this.filesToShow = folder.files;

            if (!openFolderFromBreadcrumbClick) {
                // When we open a new folder, we need to put it into history
                this.fileExplorerHistoryService.addToHistory(folder);
            } else {
                // When we click on the breadcrumb, we cut the rest of the breadcrumb
                this.fileExplorerHistoryService.goBackInHistoryTo(folder);
            }
        } else {
            this.openRootFolder();
        }
    }

    /**
     * Select all files of a certain folder
     */
    selectAllFilesOfFolder(folder: FolderViewModel): void {
        if (folder.files.length > 0) {
            this.onMultipleFilesSelected.emit(folder.files);
        }
    }

    /**
     * Complets a Subject (injected in takeUntil in requests) in order to cancel all currently ongoing subscriptions.
     * Used to cancel all requests when switching tabs to avoid
     * showing wrong files in a tab (i.e. when a request takes too long) (422337)
     */
    private cancelCurrentRequests(): void {
        this.subscriptions.forEach((sub: Subscription) => {
            if (sub) {
                sub.unsubscribe();
            }
        });
    }

    private clearErrorMessage(): void {
        this.errorMessage = undefined;
    }

    private clearFoldersAndFiles(): void {
        this.filesToShow = [];
        this.foldersToShow = [];
    }

    private openRootFolder(): void {
        this.foldersToShow = this.allFolders;

        // Root folder never has files. If this changes, add them here.
        this.filesToShow = [];
    }

    private searchResults(): void {
        this.isResultsLoading = true;

        const searchResults$ = this.resultsService
            .searchGroup$(this.currentResultSearchParams)
            .pipe(
                tap((res: ListResponse<ResultGroup>) => {
                    this.isResultsLoading = false;
                    this.resultsTotalCount = res?.meta?.totalCount ?? 0;
                    this.allFolders = mapAllGroupResultsToFolders(res.data);
                    this.openFolder();
                }),
            )
            .subscribe();

        this.subscriptions.push(searchResults$);
    }

    private mapSearchParamsToHttpParams(searchParams: ListSearch): HttpParams {
        const params = new HttpParams()
            .set('page[number]', searchParams.pageNumber.toString())
            .set('page[size]', searchParams.pageSize.toString())
            .set('sort', searchParams.sortBy)
            .set('title', searchParams.searchQuery)
            .set('text', searchParams.searchQuery)
            .set('access', 'read');

        return params;
    }

    private getDefaultResultSearchParams(): HttpParams {
        return new HttpParams().set('sort', '-finishedAt').append('access', 'read');
    }
}

export enum FileExplorerTabs {
    MY_FILES = 'MY_FILES',
    MY_RESULTS = 'MY_RESULTS',
    MY_DATASETS = 'MY_DATASETS',
    DATA = 'DATA',
}
